"use client";

import { useTranslator } from "~/i18n/provider";

import { type KeyboardEvent, useMemo, useRef, useState } from "react";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { SizeButton } from "~/components/product/variant/SizeButton";
import { SizeGuideDialog } from "~/components/product/variant/SizeGuideDialog";
import { sizeStock } from "~/components/product/variant/size-stock";
import { useVariant } from "~/components/product/variant/useVariant";
import { LOW_STOCK_THRESHOLD } from "~/config/storefront";
import type { PdpOption } from "~/server/api/schemas/pdp";

const ARROW_NEXT = new Set(["ArrowRight", "ArrowDown"]);
const ARROW_PREV = new Set(["ArrowLeft", "ArrowUp"]);

/**
 * Size buttons for a single SIZE option (P3-06). An ARIA radiogroup mirroring
 * {@link SwatchGroup}: roving tabindex, arrow keys move to the next *available*
 * size (wrapping) and select it, Space/Enter selects the focused one. Selection
 * flows through the P3-04 engine ({@link useVariant}) — combined with the chosen
 * colour it resolves the variant and updates price/stock. A live caption names
 * the hovered/selected size's low-stock state ("Only N left"). The size-guide
 * link renders only when content exists and the brand flag is on.
 */
export function SizeSelector({
  option,
  sizeGuideHtml,
  className,
}: {
  option: PdpOption;
  /**
   * Pre-sanitised size-guide HTML from the projection; "" when none. Typed
   * optional to tolerate a stale pre-field cache during rollout (coalesced below).
   */
  sizeGuideHtml: string | undefined;
  className?: string;
}) {
  const t = useTranslator();
  const { selected, availability, selectOption } = useVariant();
  const sizeGuideFlag = useFeatureFlag("pdpSizeGuide");
  const selectedValueId = selected[option.id] ?? null;
  const [hoveredValueId, setHoveredValueId] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Coalesce defensively: a PDP projection cached before this field shipped
  // (rollout window / stale cache) can arrive without `sizeGuideHtml`.
  const guideHtml = sizeGuideHtml ?? "";
  const showSizeGuide = sizeGuideFlag && guideHtml.length > 0;

  const isAvailable = (valueId: string) =>
    availability[valueId]?.selectable ?? availability[valueId]?.available ?? false;

  const activeIndex = useMemo(() => {
    const selectedIndex = option.values.findIndex(
      (value) => value.id === selectedValueId,
    );
    if (selectedIndex !== -1) return selectedIndex;
    const firstAvailable = option.values.findIndex((value) =>
      isAvailable(value.id),
    );
    return firstAvailable === -1 ? 0 : firstAvailable;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option.values, selectedValueId, availability]);

  const focusAndSelect = (index: number) => {
    const value = option.values[index];
    if (!value) return;
    buttonRefs.current[index]?.focus();
    if (isAvailable(value.id)) selectOption(option.id, value.id);
  };

  const moveFocus = (fromIndex: number, step: 1 | -1) => {
    const count = option.values.length;
    for (let hops = 1; hops <= count; hops++) {
      const next = (fromIndex + step * hops + count * hops) % count;
      const candidate = option.values[next];
      if (candidate && isAvailable(candidate.id)) {
        focusAndSelect(next);
        return;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = buttonRefs.current.indexOf(event.target as HTMLButtonElement);
    if (index === -1) return;
    if (ARROW_NEXT.has(event.key)) {
      event.preventDefault();
      moveFocus(index, 1);
    } else if (ARROW_PREV.has(event.key)) {
      event.preventDefault();
      moveFocus(index, -1);
    } else if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const value = option.values[index];
      if (value && isAvailable(value.id)) selectOption(option.id, value.id);
    }
  };

  const openGuide = () => {
    setGuideOpen(true);
  };

  // Caption reflects the hovered size, else the selection — low-stock only.
  const captionValueId = hoveredValueId ?? selectedValueId;
  const caption = captionValueId
    ? sizeStock(availability[captionValueId], LOW_STOCK_THRESHOLD)
    : null;

  return (
    <div className={className} data-testid={`pdp-sizes-${option.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-fg">{option.name}</p>
        {showSizeGuide && (
          <button
            type="button"
            onClick={openGuide}
            data-testid="pdp-size-guide-link"
            className="text-sm font-medium text-primary underline underline-offset-4 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {t("pdp.sizeGuide")}
          </button>
        )}
      </div>

      <div
        role="radiogroup"
        aria-label={option.name}
        onKeyDown={onKeyDown}
        onMouseLeave={() => setHoveredValueId(null)}
        className="mt-2 flex flex-wrap gap-2"
      >
        {option.values.map((value, index) => (
          <SizeButton
            key={value.id}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            value={value}
            selected={value.id === selectedValueId}
            selectable={isAvailable(value.id)}
            selectionHint={availability[value.id]?.requiresReset ? "choose the other options again" : undefined}
            stockKind={
              availability[value.id]?.requiresReset ? "in" : sizeStock(availability[value.id], LOW_STOCK_THRESHOLD).kind
            }
            unavailableReason={isAvailable(value.id) ? "out of stock; select to view availability alerts" : "no variant exists"}
            tabIndex={index === activeIndex ? 0 : -1}
            onSelect={() => selectOption(option.id, value.id)}
            onHover={() => setHoveredValueId(value.id)}
          />
        ))}
      </div>

      <p aria-live="polite" className="mt-1.5 min-h-4 text-xs text-warning">
        {caption?.kind === "low" ? caption.label : ""}
      </p>

      {showSizeGuide && (
        <SizeGuideDialog
          open={guideOpen}
          onOpenChange={setGuideOpen}
          html={guideHtml}
        />
      )}
    </div>
  );
}
